import { z } from "zod";

export const INVESTING_PROFILE_PREFERENCE_KEY = "investing_profile_v1" as const;
export const PORTFOLIO_BUILDER_DRAFT_PREFERENCE_KEY =
  "portfolio_builder_draft_v1" as const;

export const InvestingPrimaryGoalSchema = z.enum([
  "growth",
  "balanced",
  "income",
  "preservation",
  "exploration",
]);
export type InvestingPrimaryGoal = z.infer<typeof InvestingPrimaryGoalSchema>;

export const InvestingTimeHorizonSchema = z.enum([
  "lt_1y",
  "1_3y",
  "3_5y",
  "5y_plus",
]);
export type InvestingTimeHorizon = z.infer<typeof InvestingTimeHorizonSchema>;

export const InvestingRiskToleranceSchema = z.enum([
  "conservative",
  "moderate",
  "aggressive",
]);
export type InvestingRiskTolerance = z.infer<typeof InvestingRiskToleranceSchema>;

export const InvestingAutomationStyleSchema = z.enum([
  "hands_on",
  "guided",
  "delegated",
]);
export type InvestingAutomationStyle = z.infer<typeof InvestingAutomationStyleSchema>;

export const InvestingStrategyInterestSchema = z.enum([
  "core_large_cap",
  "yield_income",
  "index_like",
  "defi",
  "thematic",
  "experimental",
]);
export type InvestingStrategyInterest = z.infer<typeof InvestingStrategyInterestSchema>;

export const InvestingPreferredChainSchema = z.enum([
  "base",
  "ethereum",
  "arbitrum",
  "solana",
]);
export type InvestingPreferredChain = z.infer<typeof InvestingPreferredChainSchema>;

export const InvestingGuardrailSchema = z.enum([
  "avoid_leverage",
  "avoid_bridges",
  "avoid_illiquid",
  "keep_stable_buffer",
]);
export type InvestingGuardrail = z.infer<typeof InvestingGuardrailSchema>;

export const InvestingProfileCompletionModeSchema = z.enum([
  "generated",
  "balanced_defaults",
]);
export type InvestingProfileCompletionMode = z.infer<
  typeof InvestingProfileCompletionModeSchema
>;

export const InvestingProfileQuizSchema = z.object({
  automationStyle: InvestingAutomationStyleSchema,
  primaryGoal: InvestingPrimaryGoalSchema,
  riskTolerance: InvestingRiskToleranceSchema,
  strategyInterests: z
    .array(InvestingStrategyInterestSchema)
    .min(1)
    .max(6),
  timeHorizon: InvestingTimeHorizonSchema,
});
export type InvestingProfileQuiz = z.infer<typeof InvestingProfileQuizSchema>;

export const BALANCED_DEFAULT_INVESTING_PROFILE_QUIZ: InvestingProfileQuiz = {
  automationStyle: "guided",
  primaryGoal: "balanced",
  riskTolerance: "moderate",
  strategyInterests: ["core_large_cap", "index_like"],
  timeHorizon: "5y_plus",
};

export const InvestingProfileV1Schema = z.object({
  automationStyle: InvestingAutomationStyleSchema,
  avatarId: z.string().min(1),
  completionMode: InvestingProfileCompletionModeSchema,
  guardrails: z.array(InvestingGuardrailSchema).min(1).max(4),
  headline: z.string().min(1),
  notes: z.string().nullable(),
  preferredChains: z.array(InvestingPreferredChainSchema).min(1).max(4),
  primaryGoal: InvestingPrimaryGoalSchema,
  riskTolerance: InvestingRiskToleranceSchema,
  strategyInterests: z
    .array(InvestingStrategyInterestSchema)
    .min(1)
    .max(6),
  summary: z.string().min(1),
  timeHorizon: InvestingTimeHorizonSchema,
  updatedAt: z.string().min(1),
  version: z.literal(1),
});
export type InvestingProfileV1 = z.infer<typeof InvestingProfileV1Schema>;

export const GenerateInvestingProfileInputSchema = z.object({
  avatarId: z.string().min(1),
  existingProfile: InvestingProfileV1Schema.optional(),
  notes: z.string().trim().max(2_000).nullable(),
  quiz: InvestingProfileQuizSchema,
});
export type GenerateInvestingProfileInput = z.infer<
  typeof GenerateInvestingProfileInputSchema
>;

export const GenerateInvestingProfileResponseSchema = z.object({
  headline: z.string().min(1),
  profile: InvestingProfileV1Schema,
  summary: z.string().min(1),
});
export type GenerateInvestingProfileResponse = z.infer<
  typeof GenerateInvestingProfileResponseSchema
>;
